import * as Sentry from "@sentry/nextjs"
import { AtlasInfo, AtlasPageHeaderProps, CampusTemplate, CampusTheme } from "atlas-ds"
import AtlasIcons from 'atlas-ds/atlas/assets/icons.svg'
import CampusIcons from 'atlas-ds/campus/assets/icons.svg'
import Env from "components/env"
import Footer from 'components/footer'
import Header from 'components/header'
import Intro from "components/intro"
import Nav from 'components/nav'
import Toasts from "components/toasts"
import { useSearchParams } from "next/navigation"
import { useRouter } from "next/router"
import ErrorPage from "pages/404"
import { EntrepriseContext } from 'pages/_app'
import React, { useContext, useEffect, useState } from 'react'
import { BRANCHE_CAP, CAP_COMPETENCE_URL } from "services/global"
import { Entreprise } from 'services/model'
import { GONE, NOT_FOUND } from "services/server"

interface TemplateProps {
    children: React.ReactNode
    serverError?: string
    showIntro?: boolean
    pageHeader?: React.ReactElement<AtlasPageHeaderProps>
    full?: boolean
}

export default function Template(props: TemplateProps) {
    const [entreprise, setEntreprise] = useContext(EntrepriseContext)
    const router = useRouter()
    const searchParams = useSearchParams()

    const [theme, setTheme] = useState<"campus" | "cap">()

    if (props.serverError) {
        switch (props.serverError) {
            case NOT_FOUND:
                return <ErrorPage statusCode={404} />
            case GONE:
                return <ErrorPage statusCode={410} />
            default:
                return <ErrorPage />
        }
    }

    const defineEntreprise = (entreprise: Entreprise | null) => {
        setEntreprise(entreprise)
        Sentry.setContext("entreprise", {
            name: entreprise?.name,
            siret: entreprise?.siret,
            branche: entreprise?.branche?.valueName,
        })
    }

    const selectEntreprise = (e: Entreprise) => {
        const currentEntreprise = entreprise as Entreprise
        localStorage.setItem("currentCompany", JSON.stringify(e))
        defineEntreprise(e)

        if (!currentEntreprise || currentEntreprise.branche !== e.branche)
            router.push(e.branche?.valueName === BRANCHE_CAP ? CAP_COMPETENCE_URL : "/", undefined,
                { shallow: true })
    }

    useEffect(() => setTheme(localStorage.getItem("theme") as "campus" | "cap" ?? "campus"), [])

    useEffect(() => {
        // En mode connecté, utiliser l'entreprise pour le thème cap
        if (entreprise) {
            const isBrancheCap = entreprise?.branche?.valueName === BRANCHE_CAP
            const theme = isBrancheCap ? "cap" : "campus"
            setTheme(theme)
            localStorage.setItem("theme", theme)
        }

        // Sur la page d'accueil en mode non connecté, vérifier l'URL pour le thème CAP
        else if (router.pathname === "/" || router.asPath.includes(CAP_COMPETENCE_URL.replace("/", ""))) {
            // utiliser router.asPath est une astuce pour conserver le thème lors du rafraichissement initial
            // pendant un instant, "capcompetence" est dans la valeur callbackUrl
            const theme = router.asPath.includes(CAP_COMPETENCE_URL.replace("/", "")) ? "cap" : "campus"
            setTheme(theme)
            localStorage.setItem("theme", theme)
        }
    }, [entreprise, router.pathname]);

    return <>
        <AtlasIcons />
        <CampusIcons />
        <Env />

        {theme && <CampusTheme theme={theme}>
            <CampusTemplate
                topNav={<Nav />}
                header={<Header entreprise={entreprise} selectEntreprise={selectEntreprise} />}
                intro={props.showIntro ? <Intro /> : undefined}
                pageHeader={props.pageHeader}
                footer={<Footer />}
                full={props.full}
                toasts={<Toasts />}
            >
                { props.serverError
                    ? <AtlasInfo type="error" title="Erreur">
                        Une erreur s'est produite. Veuillez réessayer plus tard.
                    </AtlasInfo>
                    : props.children
                }
            </CampusTemplate>
        </CampusTheme>}
    </>
}
